import * as React from "react";
import { useTheme } from "@mui/material/styles";
import { BarChart, axisClasses } from "@mui/x-charts";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";

/**
 * Chart component for displaying credits usage by day
 *
 * @param {Object[]} creditsByDate - Array of objects containing date and credits data
 * @returns {JSX.Element} - The rendered chart component
 */
export default function Chart({ creditsByDate = [] }) {
  const theme = useTheme();

  // Check if we have data to display
  if (!creditsByDate || creditsByDate.length === 0) {
    return (
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography
          variant='body1'
          color='text.secondary'
        >
          No data available for the selected period
        </Typography>
      </Box>
    );
  }

  // Format data for BarChart - ensure all required fields exist
  const data = creditsByDate
    .filter(
      (item) =>
        item && typeof item === "object" && "date" in item && "credits" in item
    )
    .map(({ date, credits }) => ({
      date: date || "Unknown",
      credits: typeof credits === "number" ? credits : 0,
    }));

  // If we have no valid data points after filtering, show the no data message
  if (data.length === 0) {
    return (
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography
          variant='body1'
          color='text.secondary'
        >
          No valid data available for the selected period
        </Typography>
      </Box>
    );
  }

  // Get max value for y-axis scale with 10% padding
  const maxCredits = Math.max(...data.map((item) => item.credits), 1);
  const yAxisMax = Math.ceil(maxCredits * 1.1);

  // Determine how many x-axis labels to show based on data length
  const xAxisTickCount =
    data.length <= 10
      ? data.length
      : data.length <= 20
      ? Math.ceil(data.length / 2)
      : Math.ceil(data.length / 3);

  // Handle chart rendering in a try-catch block to prevent runtime errors
  try {
    return (
      <React.Fragment>
        <div
          style={{
            width: "100%",
            flexGrow: 1,
            overflow: "hidden",
            height: "100%",
          }}
        >
          <BarChart
            dataset={data}
            legend={null}
            margin={{
              top: 16,
              right: 35,
              left: 70,
              bottom: 50,
            }}
            xAxis={[
              {
                scaleType: "band",
                dataKey: "date",
                tickNumber: xAxisTickCount,
                tickLabelStyle: theme.typography.body2,
                angle: -45, // Rotate labels at an angle
                dy: 10, // Adjust vertical positioning
                legendLabel: "Date",
              },
            ]}
            yAxis={[
              {
                label: "Credits",
                labelStyle: {
                  ...theme.typography.body1,
                  fill: theme.palette.text.primary,
                },
                tickLabelStyle: theme.typography.body2,
                max: yAxisMax,
              },
            ]}
            series={[
              {
                dataKey: "credits",
                color: theme.palette.primary.main,
                valueFormatter: (value) => value.toFixed(0),
              },
            ]}
            sx={{
              [`.${axisClasses.root} line`]: {
                stroke: theme.palette.text.secondary,
              },
              [`.${axisClasses.root} text`]: {
                fill: theme.palette.text.secondary,
              },
              [`& .${axisClasses.left} .${axisClasses.label}`]: {
                transform: "translateX(-25px)",
              },
              height: 190,
            }}
          />
        </div>
      </React.Fragment>
    );
  } catch (error) {
    console.error("Error rendering chart:", error);
    return (
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
        }}
      >
        <Typography
          variant='body1'
          color='error'
        >
          Error rendering chart
        </Typography>
        <Typography
          variant='body2'
          color='text.secondary'
        >
          Please try adjusting the date range or contact support
        </Typography>
      </Box>
    );
  }
}
